import { Component, ElementRef, computed, input, model, output, signal, viewChild } from '@angular/core'
import { FormValueControl } from '@angular/forms/signals'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import {
  SEGMENT_MAX,
  TimeSegment,
  formatSegment,
  parseSegment,
  stepSegment,
  typeTime,
  withTimeOfDay,
} from '../time-of-day'

/**
 * The bare time stepper, `[−][HH][+] : [−][MM][+]`, 176px. It draws no surface, so it sits inline in a form,
 * two side by side,
 * (`[formField]="form.start"`, a Signal Forms control) or inside `seiri-date-time-picker-panel` (`[(value)]`).
 *
 * - It changes only hours and minutes. The date part is the value's, or today's when there is no value; an input
 *   `Date` is never mutated, every change is a new one. Zone-free: browser-local wall-clock in and out.
 * - Hours step by 1 and wrap 0–23. Minutes step to the next or previous multiple of 5 and wrap 0–55; the hour is not
 *   carried. A value off the 5-minute grid is kept as it is until it is stepped.
 * - Typing is masked as Taiga UI's `InputTime` does it (`typeTime`): `3` in hours shows `03` and stays there, so it
 *   can be backspaced; the next digit moves to the minutes. `2` then `5` is `02:5`. A one-digit segment is padded on
 *   blur. Backspace or ← at the start of the minutes, and → at the end of the hours, cross between the two.
 * - Tab visits only the two inputs, which are spinbuttons: ↑/↓ steps them. The −/+ buttons are for pointer and touch.
 * - A `null` value shows `--`, and the first step starts from 00:00.
 */
@Component({
  selector: 'seiri-time-stepper',
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './time-stepper.component.html',
  styleUrl: './time-stepper.component.css',
  host: {
    class: 'seiri-time-stepper',
    role: 'group',
    '[attr.aria-label]': 'label()',
    '[class.seiri-time-stepper--invalid]': 'showError()',
  },
})
export class TimeStepperComponent implements FormValueControl<Date | null> {
  readonly value = model<Date | null>(null)
  readonly disabled = input(false)
  readonly invalid = input(false)
  readonly touched = input(false)
  readonly touch = output<void>()
  /** The accessible name of the group, e.g. "Od". */
  readonly label = input('Čas')

  protected readonly segmentMax = SEGMENT_MAX
  protected readonly showError = computed(() => this.invalid() && this.touched())
  protected readonly hours = computed(() => this.value()?.getHours() ?? null)
  protected readonly minutes = computed(() => this.value()?.getMinutes() ?? null)

  /** What is typed in each segment, shown as typed until that input blurs. */
  private readonly draft = signal<Partial<Record<TimeSegment, string>>>({})

  private readonly hoursInput = viewChild.required<ElementRef<HTMLInputElement>>('hours')
  private readonly minutesInput = viewChild.required<ElementRef<HTMLInputElement>>('minutes')

  protected text(segment: TimeSegment): string {
    const draft = this.draft()[segment]
    if (draft !== undefined) return draft
    const value = this.segmentValue(segment)
    return value === null ? '' : formatSegment(value)
  }

  protected segmentValue(segment: TimeSegment): number | null {
    return segment === 'hours' ? this.hours() : this.minutes()
  }

  protected step(segment: TimeSegment, direction: 1 | -1): void {
    this.draft.set({})
    const hours = this.hours() ?? 0
    const minutes = this.minutes() ?? 0
    if (segment === 'hours') {
      this.write(stepSegment('hours', hours, direction), minutes)
    } else {
      this.write(hours, stepSegment('minutes', minutes, direction))
    }
  }

  /**
   * Re-reads the input's whole text through `typeTime`, so a selection replaced, a paste or an IME composition all
   * take the same path as a key. When the text overflows into the minutes, they take it over and get the focus.
   */
  protected onInput(segment: TimeSegment, event: Event): void {
    const field = event.target as HTMLInputElement
    const entry = typeTime({ hours: '', minutes: '', segment }, field.value)
    this.setDraft(segment, entry[segment])
    field.value = entry[segment]
    if (entry.segment !== segment) {
      this.setDraft(entry.segment, entry[entry.segment])
      this.focusEnd(entry.segment, entry[entry.segment])
    }

    const hours = parseSegment(entry.hours)
    const minutes = parseSegment(entry.minutes)
    if (hours === null && minutes === null) return
    this.write(hours ?? this.hours() ?? 0, minutes ?? this.minutes() ?? 0)
  }

  protected onKeydown(segment: TimeSegment, event: KeyboardEvent): void {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      this.step(segment, event.key === 'ArrowUp' ? 1 : -1)
      return
    }
    const field = event.target as HTMLInputElement
    if (field.selectionStart !== field.selectionEnd) return
    const atStart = field.selectionStart === 0
    const atEnd = field.selectionStart === field.value.length
    if (segment === 'minutes' && atStart && (event.key === 'Backspace' || event.key === 'ArrowLeft')) {
      event.preventDefault()
      this.focusEnd('hours')
    } else if (segment === 'hours' && atEnd && event.key === 'ArrowRight') {
      event.preventDefault()
      this.focusEnd('minutes')
    }
  }

  protected onBlur(segment: TimeSegment): void {
    this.setDraft(segment, undefined)
    this.touch.emit()
  }

  private setDraft(segment: TimeSegment, text: string | undefined): void {
    this.draft.update((draft) => ({ ...draft, [segment]: text }))
  }

  /** Focuses `segment`'s input with the caret after its text, writing `text` into it first when given. */
  private focusEnd(segment: TimeSegment, text?: string): void {
    const field = (segment === 'hours' ? this.hoursInput : this.minutesInput)().nativeElement
    if (text !== undefined) field.value = text
    field.focus()
    field.setSelectionRange(field.value.length, field.value.length)
  }

  private write(hours: number, minutes: number): void {
    this.value.set(withTimeOfDay(this.value() ?? new Date(), hours, minutes))
  }
}
